import React, { useState } from "react";
import { Oval } from "react-loader-spinner";
import { MdClose, MdPersonAdd, MdDelete, MdSearch, MdEmail } from "react-icons/md";
import {
  useGetCommunityAdminsQuery,
  useSearchAdminByEmailMutation,
  useInviteCommunityAdminMutation,
  useRemoveCommunityAdminMutation,
} from "../../redux/slices/api/communityApiSlice";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import notify from "../common/notify";

const AdminManagement = ({ communityId, onClose }) => {
  const [email, setEmail] = useState("");
  const [searchResult, setSearchResult] = useState(null);

  const { data: adminsData, isLoading: adminsLoading } = useGetCommunityAdminsQuery({ id: communityId });
  const [searchAdmin, { isLoading: searchLoading }] = useSearchAdminByEmailMutation();
  const [inviteAdmin, { isLoading: inviteLoading }] = useInviteCommunityAdminMutation();
  const [removeAdmin, { isLoading: removeLoading }] = useRemoveCommunityAdminMutation();

  const admins = adminsData?.admins || [];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      const result = await searchAdmin({ id: communityId, email: email.trim() }).unwrap();
      setSearchResult(result);
    } catch (err) {
      notify("error", err?.data?.message || "Search failed");
    }
  };

  const handleInvite = async () => {
    try {
      await inviteAdmin({ id: communityId, email: email.trim() }).unwrap();
      notify("success", searchResult?.found
        ? "Admin added successfully"
        : "Invitation email sent successfully"
      );
      setEmail("");
      setSearchResult(null);
    } catch (err) {
      notify("error", err?.data?.message || "Failed to invite admin");
    }
  };

  const handleRemove = async (adminId) => {
    if (!window.confirm("Are you sure you want to remove this admin?")) return;

    try {
      await removeAdmin({ id: communityId, adminId }).unwrap();
      notify("success", "Admin removed successfully");
    } catch (err) {
      notify("error", err?.data?.message || "Failed to remove admin");
    }
  };

  const getUserName = (admin) => {
    if (admin.user) {
      return admin.user.fundername || `${admin.user.firstname || ""} ${admin.user.lastname || ""}`.trim();
    }
    return admin.email;
  };

  const getUserImage = (admin) => {
    return admin.user?.images?.profile || UserPlaceHolderImg;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-900">Manage Admins</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <MdClose className="text-xl text-gray-500" />
          </button>
        </div>

        {/* Search Section */}
        <div className="p-5 border-b">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="email"
                placeholder="Search by email address..."
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSearchResult(null);
                }}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#8538ff] focus:ring-1 focus:ring-[#8538ff] outline-none text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={searchLoading || !email.trim()}
              className="px-4 py-2.5 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {searchLoading ? (
                <Oval height={18} width={18} color="#fff" secondaryColor="#ddd" strokeWidth={4} />
              ) : (
                "Search"
              )}
            </button>
          </form>

          {/* Search Result */}
          {searchResult && (
            <div className="mt-3">
              {searchResult.alreadyAdmin && (
                <p className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                  This user is already an admin of this community.
                </p>
              )}
              {searchResult.isSelf && (
                <p className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                  You cannot add yourself as an admin.
                </p>
              )}
              {!searchResult.alreadyAdmin && !searchResult.isSelf && searchResult.found && (
                <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={searchResult.user?.images?.profile || UserPlaceHolderImg}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-sm text-gray-900">
                        {searchResult.user?.fundername ||
                          `${searchResult.user?.firstname || ""} ${searchResult.user?.lastname || ""}`.trim()}
                      </p>
                      <p className="text-xs text-gray-500">{searchResult.user?.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleInvite}
                    disabled={inviteLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {inviteLoading ? (
                      <Oval height={14} width={14} color="#fff" secondaryColor="#ddd" strokeWidth={4} />
                    ) : (
                      <>
                        <MdPersonAdd />
                        Add Admin
                      </>
                    )}
                  </button>
                </div>
              )}
              {!searchResult.alreadyAdmin && !searchResult.isSelf && !searchResult.found && (
                <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <MdEmail className="text-gray-400 text-lg" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-900">{email}</p>
                      <p className="text-xs text-gray-500">Not registered on FunDeSci</p>
                    </div>
                  </div>
                  <button
                    onClick={handleInvite}
                    disabled={inviteLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {inviteLoading ? (
                      <Oval height={14} width={14} color="#fff" secondaryColor="#ddd" strokeWidth={4} />
                    ) : (
                      <>
                        <MdEmail />
                        Send Invite
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Admins List */}
        <div className="flex-1 overflow-y-auto p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Current Admins {admins.length > 0 && `(${admins.length})`}
          </h3>

          {adminsLoading ? (
            <div className="flex justify-center py-8">
              <Oval height={32} width={32} color="#8538ff" secondaryColor="#ddd" strokeWidth={4} />
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-8">
              <MdPersonAdd className="text-4xl text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No admins assigned yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Search by email above to add admins
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {admins.map((admin) => (
                <div
                  key={admin._id}
                  className="flex items-center justify-between bg-gray-50 rounded-xl p-3"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={getUserImage(admin)}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-sm text-gray-900">
                        {getUserName(admin)}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500">{admin.email}</p>
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            admin.status === "active"
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {admin.status === "active" ? "Active" : "Pending"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(admin._id)}
                    disabled={removeLoading}
                    className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove admin"
                  >
                    <MdDelete className="text-lg" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;
